import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { parseAsString, useQueryState } from 'nuqs';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { SectionLabel, PageDescription } from '@reflex/ui/components/layout/Typography';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { SecretInputDialog } from '@/components/forms/SecretInputDialog';
import { Card } from '@reflex/ui/components/ui/card';
import { MissingSecretsBanner } from './MissingSecretsBanner';
import { IntegrationSecretGroup } from './IntegrationSecretGroup';
import type { IntegrationSecretStatus, SecretStatusItem } from '@/lib/types';

/**
 * Group service-tier integrations by source so super-admins see a stable
 * structure ("Sign-in providers" vs "GitHub App" vs anything else) without
 * the server having to ship category sub-types. The grouping is derived
 * purely from the integration `id` shape:
 *
 * - `plugin-auth-*-service` → sign-in provider OAuth client credentials
 * - `github-app`            → static GitHub App registry slot
 * - any other `category: 'service'` → "Other"
 */
function groupIntegrations(integrations: IntegrationSecretStatus[]): {
  signInProviders: IntegrationSecretStatus[];
  githubApp: IntegrationSecretStatus[];
  other: IntegrationSecretStatus[];
} {
  const signInProviders: IntegrationSecretStatus[] = [];
  const githubApp: IntegrationSecretStatus[] = [];
  const other: IntegrationSecretStatus[] = [];
  for (const integration of integrations) {
    if (integration.id.startsWith('plugin-auth-') && integration.id.endsWith('-service')) {
      signInProviders.push(integration);
    } else if (integration.id === 'github-app') {
      githubApp.push(integration);
    } else {
      other.push(integration);
    }
  }
  return { signInProviders, githubApp, other };
}

function IntegrationGroups({
  integrations,
  focusedIntegrationId,
  onEditSecret,
  isPending,
}: {
  integrations: IntegrationSecretStatus[];
  focusedIntegrationId: string | null;
  onEditSecret: (item: SecretStatusItem) => void;
  isPending: boolean;
}) {
  return (
    <div className="space-y-3">
      {integrations.map((integration) => {
        const isFocused = focusedIntegrationId === integration.id;
        return (
          <IntegrationSecretGroup
            key={integration.id}
            integration={integration}
            onEditSecret={onEditSecret}
            isPending={isPending}
            defaultOpen={isFocused || !integration.ready}
          />
        );
      })}
    </div>
  );
}

export function ServiceSecretsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [focusedIntegrationId] = useQueryState('integration', parseAsString);
  const registryQuery = useQuery({
    queryKey: queryKeys.serviceSecretRegistry,
    queryFn: api.getServiceSecretRegistry,
  });

  const setMutation = useMutation({
    mutationFn: ({ name, value }: { name: string; value: string; redirectAfterSet?: string }) =>
      api.setServiceSecret(name, value),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceSecretRegistry });
      queryClient.invalidateQueries({ queryKey: queryKeys.secretsStatus });
      queryClient.invalidateQueries({ queryKey: ['plugins'] });
      setEditing(null);
      if (variables.redirectAfterSet) {
        toast.success('Saved. Continue setup on the plugin page.');
        navigate(variables.redirectAfterSet);
        return;
      }
      toast.success('Service secret saved');
    },
    onError: (err: Error) =>
      toast.error('Failed to save service secret', { description: err.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => api.deleteServiceSecret(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceSecretRegistry });
      queryClient.invalidateQueries({ queryKey: queryKeys.secretsStatus });
      queryClient.invalidateQueries({ queryKey: ['plugins'] });
      toast.success('Service secret deleted');
    },
    onError: (err: Error) =>
      toast.error('Failed to delete service secret', { description: err.message }),
  });

  const [editing, setEditing] = useState<SecretStatusItem | null>(null);

  const integrations = registryQuery.data ?? [];
  const grouped = groupIntegrations(integrations);
  const isPending = setMutation.isPending || deleteMutation.isPending;

  useEffect(() => {
    if (!focusedIntegrationId || registryQuery.isLoading || !registryQuery.data) return;
    const el = document.getElementById(`integration-secret-group-${focusedIntegrationId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusedIntegrationId, registryQuery.isLoading, registryQuery.data]);

  return (
    <AdminSubPageLayout
      title="Service Secrets"
      description="Operator-managed credentials (sign-in provider OAuth apps, GitHub App, internal infra). These are walled off from organizations: never mirrored into Runloop and never visible on the org Secrets page."
      docs="administration/security#secrets-tiers"
    >
      <AsyncContent
        isLoading={registryQuery.isLoading}
        isError={registryQuery.isError}
        loadingMessage="Loading service secrets…"
        errorMessage="Failed to load service secrets."
      >
        <div className="space-y-8">
          <Card className="border-dashed bg-muted/30 p-4">
            <div className="flex items-start gap-3">
              <Lock className="icon-md mt-0.5 text-muted-foreground" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Service-tier wall</p>
                <PageDescription className="text-xs">
                  Values stored here are scoped to <code>scope=&apos;service&apos;</code> in the
                  database and only resolvable through the service secret manager. They never reach
                  Runloop, never appear in <code>/secrets/available</code>, and cannot be referenced
                  from agent env-var <code>secretRef</code>s.
                </PageDescription>
              </div>
            </div>
          </Card>

          <MissingSecretsBanner integrations={integrations} />

          {grouped.signInProviders.length > 0 && (
            <section>
              <SectionLabel className="mb-3">Sign-in providers</SectionLabel>
              <IntegrationGroups
                integrations={grouped.signInProviders}
                focusedIntegrationId={focusedIntegrationId}
                onEditSecret={setEditing}
                isPending={isPending}
              />
            </section>
          )}

          {grouped.githubApp.length > 0 && (
            <section>
              <SectionLabel className="mb-3">GitHub App</SectionLabel>
              <IntegrationGroups
                integrations={grouped.githubApp}
                focusedIntegrationId={focusedIntegrationId}
                onEditSecret={setEditing}
                isPending={isPending}
              />
            </section>
          )}

          {grouped.other.length > 0 && (
            <section>
              <SectionLabel className="mb-3">Other</SectionLabel>
              <IntegrationGroups
                integrations={grouped.other}
                focusedIntegrationId={focusedIntegrationId}
                onEditSecret={setEditing}
                isPending={isPending}
              />
            </section>
          )}
        </div>
      </AsyncContent>

      {editing && (
        <SecretInputDialog
          open={!!editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          secretKey={editing.key}
          label={editing.label}
          description={editing.description}
          docsUrl={editing.docsUrl}
          isUpdate={editing.isSet}
          sensitive={editing.sensitive !== false}
          onSubmit={(value) =>
            setMutation.mutate({
              name: editing.key,
              value,
              redirectAfterSet: editing.redirectAfterSet,
            })
          }
          isPending={setMutation.isPending}
        />
      )}
    </AdminSubPageLayout>
  );
}
